import {useGetPublicAgencyCustomerTerms} from "../../../../lib/api-client-react/src/agency/agency";
import { useSEO } from "@/hooks/use-seo";
import { ChevronLeft } from "lucide-react";

export default function TermsOfService() {
  useSEO({
    title: "Terms of Service — Ayrshire Hub",
    description: "Terms and conditions for using Ayrshire Hub.",
    noIndex: false,
  });

  const published=useGetPublicAgencyCustomerTerms<{current:{version:string;content:string;contentHash:string;effectiveAt:string}|null}>({query:{queryKey:['public-agency-customer-terms'],retry:false}});
  if(published.data?.current){const doc=published.data.current;return <div className="max-w-3xl mx-auto px-4 py-8"><h1 className="text-3xl font-bold">Terms of Service</h1><p className="mt-3 text-sm text-slate-500">Version {doc.version} · Effective {new Date(doc.effectiveAt).toLocaleString('en-GB')}</p><pre className="mt-6 whitespace-pre-wrap break-words font-sans text-sm leading-7">{doc.content}</pre></div>;}

  const updated = "26 October 2026 — factual payment and support clarifications";

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <button onClick={() => window.history.back()} className="flex items-center gap-1 text-sm text-slate-500 hover:text-primary transition-colors mb-6 -ml-1">
        <ChevronLeft size={18} /> Back
      </button>
      <h1 className="text-3xl font-bold text-primary mb-2">Terms of Service</h1>
      <p className="text-slate-500 text-sm mb-8">Last updated: {updated}</p>

      <div className="prose prose-slate max-w-none space-y-8 text-slate-700 leading-relaxed">

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">1. Acceptance of terms</h2>
          <p>
            Ayrshire Hub is a trading name of <strong>Scotdev Ltd</strong> (Company No. SC887365), a company registered in Scotland
            ("<strong>we</strong>", "<strong>us</strong>", "<strong>our</strong>").
            By accessing or using Ayrshire Hub (the "Service"), you agree to be bound by these Terms of Service.
            If you do not agree, please do not use the Service.
            We may update these terms from time to time; continued use after changes constitutes acceptance.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">2. Eligibility</h2>
          <p>
            You must be at least 16 years old to create an account and use the Service.
            By registering, you confirm that you meet this requirement.
            Business and tradesperson accounts must be operated by individuals aged 18 or over
            and must accurately represent their business or trade.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">3. Your account</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>You are responsible for keeping your password secure and for all activity under your account.</li>
            <li>You must provide accurate information when registering.</li>
            <li>You may not create accounts for others without their consent or impersonate another person or business.</li>
            <li>Notify us immediately at <a href="mailto:hello@ayrshirehub.co.uk" className="text-primary underline">hello@ayrshirehub.co.uk</a> if you suspect unauthorised access to your account.</li>
          </ul>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">4. Acceptable use</h2>
          <p>You agree not to:</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>Post content that is illegal, harmful, threatening, abusive, defamatory, or harassing</li>
            <li>Post spam, unsolicited advertising, or misleading information</li>
            <li>Infringe any copyright, trademark, or other intellectual property rights</li>
            <li>Attempt to access accounts or data that do not belong to you</li>
            <li>Use automated tools to scrape or extract data from the Service</li>
            <li>Post content involving child exploitation or abuse</li>
            <li>Use the Service for any unlawful purpose</li>
            <li>Post false or misleading reviews of businesses</li>
          </ul>
          <p className="mt-3">
            We reserve the right to remove content and suspend or terminate accounts that violate these rules,
            with or without prior notice.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">5. User content</h2>
          <p>
            You retain ownership of content you post (posts, photos, listings). By posting, you grant Ayrshire Hub
            a non-exclusive, royalty-free, worldwide licence to display, distribute and promote that content
            within the Service. You represent that you have the right to post the content and that it does not
            infringe third-party rights.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">6. Paid features</h2>
          <h3 className="text-base font-semibold text-slate-800 mt-3 mb-2">6.1 Job leads (tradesperson subscriptions)</h3>
          <p>
            Tradespeople can unlock job leads by paying a one-off fee. Once unlocked, contact details are revealed.
            Payments are non-refundable once a lead has been unlocked and contact information disclosed.
          </p>
          <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">6.2 Listing and sponsorship fees</h3>
          <p>
            Fees for posting deals, events or sponsored/featured listings are charged as displayed at the time of submission.
            Approved listings are non-refundable. Listings that are rejected by our moderation team will receive a full refund.
          </p>
          <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">6.3 Payment processing</h3>
          <p>
            All payments are processed securely by Stripe. By making a payment you also agree to{" "}
            <a href="https://stripe.com/gb/legal" target="_blank" rel="noopener noreferrer" className="text-primary underline">
              Stripe's Terms of Service
            </a>.
          </p>
          <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">6.4 Statutory cancellation rights</h3>
          <p>
            Under UK consumer law, you normally have 14 days to cancel a contract. However, by requesting immediate
            access to digital content (lead contact details, live listings), you acknowledge that the 14-day right
            to cancel does not apply once the digital content has been provided.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">7. Order Local — food and retail ordering</h2>
          <h3 className="text-base font-semibold text-slate-800 mt-3 mb-2">7.1 Marketplace only</h3>
          <p>
            Ayrshire Hub's "Order Local" feature is a <strong>local marketplace</strong> that connects customers
            directly with local food and retail businesses. We provide the technology platform; we are not a party
            to the contract for the supply of goods between you and the store.
          </p>
          <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">7.2 Payments</h3>
          <p className="text-sm text-slate-600 leading-relaxed">Depending on the payment route, card payments are processed through the shop’s Stripe Connect account or collected through the platform and allocated under the existing payment arrangements. Cash is available where offered. The payment route does not by itself identify every legal supplier. Item prices, the separate platform service fee and any delivery or shop charges are shown before you pay.</p>
          <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">7.3 Refunds and support</h3>
          <p className="text-sm text-slate-600 leading-relaxed">The named shop is responsible for the goods it supplies. You can contact the shop or Ayrshire Hub support about an order problem. Ayrshire Hub can investigate and process authorised refunds through the existing payment arrangements. The responsible supplier and your statutory rights determine the remedy; an internal allocation of costs does not remove those rights. Ayrshire Hub remains responsible for its own services and promises.</p>
          <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">7.4 Store responsibility</h3>
          <p>
            Stores listed on Order Local are responsible for the accuracy of their menu, pricing, allergen information,
            and fulfilment of orders. Ayrshire Hub does not verify this information. If you have a food allergy or
            dietary requirement, please contact the store directly before placing an order.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">8. Delivery drivers</h2>
          <h3 className="text-base font-semibold text-slate-800 mt-3 mb-2">8.1 Requirements</h3>
          <p>
            You may not accept or carry out deliveries through Ayrshire Hub unless, at all times while doing so, you hold
            and maintain all of the following:
          </p>
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li>
              <strong>Motor insurance valid for delivery use.</strong> Your policy must expressly cover carriage of goods
              for hire and reward. Ordinary social, domestic, pleasure and commuting cover is{" "}
              <strong>not sufficient</strong> and does not permit you to deliver.
            </li>
            <li>
              <strong>An insulated heat bag.</strong> Every food delivery must be carried in a clean, undamaged insulated
              thermal bag large enough for the order. Food must not be carried loose in a vehicle, in a footwell, or in
              any container that is not temperature retaining.
            </li>
            <li>A full and valid UK driving licence for the vehicle you use, and a vehicle that is roadworthy, taxed and holds a current MOT where required.</li>
            <li>The legal right to work in the United Kingdom.</li>
          </ul>
          <p className="mt-3">
            You must produce evidence of any of the above on request. We may suspend or permanently remove your access to
            deliveries, without notice, if you cannot do so or if you no longer meet these requirements. It is your
            responsibility — not ours — to keep your insurance current and appropriate for the work you carry out.
          </p>

          <h3 className="text-base font-semibold text-slate-800 mt-4 mb-2">8.2 Care of orders</h3>
          <p>
            Orders must be kept in the heat bag from collection until handover, must not be opened, tampered with or
            interfered with, and must be delivered to the address given. Where a delivery code is issued, it must be
            obtained from the customer at the point of handover.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">9. Tradesperson & business accounts</h2>
          <p>
            Businesses and tradespeople must accurately represent their services, qualifications, and coverage areas.
            False or misleading representations may result in account suspension.
            Ayrshire Hub does not verify credentials or insurances; residents should carry out their own due diligence.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">10. Community moderation</h2>
          <p>
            Community posts and comments are moderated. Our team and appointed moderators may remove content
            that violates these terms. Users can report content using the flag/report button.
            Repeated violations may result in a temporary or permanent community ban.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">11. Intellectual property</h2>
          <p>
            The Ayrshire Hub name, logo, and platform design are the intellectual property of Scotdev Ltd.
            You may not copy, reproduce, or distribute them without our prior written consent.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">12. Disclaimers</h2>
          <p>
            The Service is provided "as is". We do not guarantee that the Service will be uninterrupted or error-free.
            We are not responsible for the accuracy of user-generated content, the quality of services provided by
            listed businesses or tradespeople, or any disputes between users.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">13. Limitation of liability</h2>
          <p>
            To the maximum extent permitted by law, Scotdev Ltd shall not be liable for any indirect, incidental,
            special, or consequential damages arising from your use of the Service, including loss of profits,
            data, or goodwill. Our total liability for any claim shall not exceed the amount you paid us in the
            12 months preceding the claim, or £100, whichever is greater.
          </p>
          <p className="mt-2">
            <strong>Loss of or damage to items.</strong> Ayrshire Hub is a marketplace. Food and other goods are
            prepared and supplied by the store and, where delivery is used, carried by independent drivers. To the
            maximum extent permitted by law we accept no liability for the loss, theft, damage, spoilage,
            contamination, deterioration, lateness or non-arrival of any food item or other goods, however caused.
            Responsibility for the goods rests with the store that supplied them and the driver who carried them.
          </p>
          <p className="mt-2">
            Nothing in these terms limits or excludes liability for death or personal injury caused by negligence,
            fraud, or any other liability that cannot be limited by law. If you are a consumer, this includes your
            statutory rights in respect of goods and services, which cannot be excluded or restricted.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">14. Marketing communications</h2>
          <p>
            When you create an account or place an order, you are <strong>automatically opted in</strong> to receive
            marketing communications from Ayrshire Hub by SMS, email and push notification. These may include local
            offers, new stores, events and service updates.
          </p>
          <p className="mt-2">
            <strong>You can opt out at any time</strong>, free of charge, by any of the following:
          </p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>
              Emailing{" "}
              <a href="mailto:hello@ayrshirehub.co.uk" className="text-primary underline">hello@ayrshirehub.co.uk</a>
            </li>
            <li>
              Sending a{" "}
              <a href="/support" className="text-primary underline">support request</a>{" "}
              via the website or the app
            </li>
          </ul>
          <p className="mt-3">
            Opting out of marketing does not stop essential service messages — order confirmations, delivery updates,
            security notices and account notifications — which we must send to provide the Service.
          </p>
          <p className="mt-2">
            We action opt-out requests promptly and, in any event, within the period required by the Privacy and
            Electronic Communications Regulations 2003 and UK GDPR.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">15. Termination</h2>
          <p>
            You may delete your account at any time from your Account Settings page. We may suspend or terminate
            your account if you breach these terms, with or without notice depending on severity.
            Upon termination, your access to the Service ceases, though content you have posted may remain
            visible for a period as described in our Privacy Policy.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">16. Governing law</h2>
          <p>
            These terms are governed by the laws of Scotland. Any disputes shall be subject to the exclusive
            jurisdiction of the Scottish courts.
          </p>
        </section>

        <section>
          <h2 className="text-xl font-semibold text-primary mb-3">17. Contact</h2>
          <p>
            For any questions about these terms, contact us at{" "}
            <a href="mailto:hello@ayrshirehub.co.uk" className="text-primary underline">hello@ayrshirehub.co.uk</a>.
          </p>
        </section>

      </div>
    </div>
  );
}
